import { QueryClient } from '@tanstack/query-core'
import type { Collection, CollectionStatus } from '@tanstack/vue-db'
import { defineStore } from 'pinia'
import { computed, markRaw, ref, watch } from 'vue'
import { useRuntimeConfig } from '#imports'
import { useEndpointContext } from '../endpoints'
import { inferRuntimeShapeUrlFromEnv, resolveRuntimeShapeUrl } from '../utils/runtimeConfig'
import type { CollectionAcquisition } from './CollectionRegistry/CollectionAcquisition.interface'
import { buildCollection } from './CollectionRegistry/CollectionBuilder'
import { buildCollectionId } from './CollectionRegistry/CollectionBuilder.helpers'
import type { CollectionBuilder, CollectionBuildRequestConfig } from './CollectionRegistry/CollectionBuilder.types'
import type { CollectionEntry } from './CollectionRegistry/CollectionEntry.interface'
import type { CollectionFactoryContext } from './CollectionRegistry/CollectionFactoryContext.interface'
import type { CollectionRequest } from './CollectionRegistry/CollectionRequest.interface'
import { registerAllCollectionBuilders } from './CollectionRegistry/registerBuilders'
import { descriptorFromRequest } from './utils/registry'
import { toAbsoluteUrl } from './utils/url'

interface CollectionBuilderEntry {
  baseConfig: CollectionBuildRequestConfig
  builder: CollectionBuilder<Collection<any, any, any>>
}

const useCollectionRegistry = defineStore('collection-registry', () => {
  const runtime = useRuntimeConfig()
  const context = ref<CollectionFactoryContext | null>(null)
  const endpoint = useEndpointContext()
  const configShapeUrl = resolveRuntimeShapeUrl(runtime.public?.reactiveDbShapeUrl)
  const envShapeUrl = inferRuntimeShapeUrlFromEnv()
  const rawShapeUrl = computed(() => configShapeUrl || envShapeUrl || endpoint.data.value?.reactiveDbShapeUrl)
  const queryClient = markRaw(new QueryClient())

  watch(
    rawShapeUrl,
    (url) => {
      if (!url) return
      const shapeUrl = toAbsoluteUrl(url) || url
      context.value = markRaw({
        shapeUrl,
        queryClient,
      })
    },
    { immediate: true },
  )

  const builders = new Map<string, CollectionBuilderEntry>()
  // Cache stores instantiated collection entries, not builder entries
  const cache = new Map<string, CollectionEntry<Collection<any, any, any>>>()
  let buildersRegistered = false

  const registeredTables = ref<string[]>([])
  const activeCollections = ref<string[]>([])
  const contextSummary = computed(() => {
    if (!context.value) return null
    return {
      shapeUrl: context.value.shapeUrl,
    }
  })
  const isInitialized = computed(() => context.value !== null)

  const updateRegisteredTables = () => {
    registeredTables.value = Array.from(builders.keys())
  }

  function register(table: string, baseConfig: CollectionBuildRequestConfig) {
    builders.set(table, {
      baseConfig,
      builder: buildCollection as CollectionBuilder<Collection<any, any, any>>,
    })
    updateRegisteredTables()
  }

  const updateActiveCollections = () => {
    activeCollections.value = Array.from(cache.keys())
  }

  function ensureBuildersRegistered() {
    if (buildersRegistered) return
    buildersRegistered = true
    registerAllCollectionBuilders(register)
  }

  function ensureContext(): CollectionFactoryContext {
    if (!context.value) throw new Error('Collection registry has not been initialized')
    return context.value as CollectionFactoryContext
  }

  function acquire<TCollection extends Collection<any, any, any>>(
    request: CollectionRequest,
  ): CollectionAcquisition<TCollection> {
    ensureBuildersRegistered()
    const builder = builders.get(request.table)
    if (!builder) throw new Error(`No builder registered for table ${request.table}`)

    const mergedRequest: CollectionBuildRequestConfig = {
      ...builder.baseConfig,
      ...request,
      where: {
        ...(builder.baseConfig.where ?? {}),
        ...(request.where ?? {}),
      },
    }
    const id = buildCollectionId(mergedRequest)
    const descriptor = descriptorFromRequest(request)

    let entry = cache.get(id) as CollectionEntry<TCollection> | undefined

    if (!entry) {
      const ctx = ensureContext()
      const collectionConfig: CollectionBuildRequestConfig = {
        ...mergedRequest,
        key: builder.baseConfig.key,
      }
      const collection = markRaw(builder.builder(ctx, collectionConfig) as TCollection)
      const createdEntry: CollectionEntry<TCollection> = {
        descriptor,
        collection,
      }
      cache.set(id, createdEntry as CollectionEntry<Collection<any, any, any>>)
      updateActiveCollections()
      entry = createdEntry
      entry.collection.startSyncImmediate?.()
    }

    return { collection: entry.collection }
  }

  function evict(id: string) {
    const entry = cache.get(id)
    if (!entry) return false

    const collection = entry.collection as unknown as Record<string, unknown>
    for (const method of ['stopSyncImmediate', 'stop', 'destroy']) {
      const fn = collection[method]
      if (typeof fn !== 'function') continue
      try {
        ;(fn as () => void).call(collection)
      } catch {
        // best effort cleanup only
      }
    }

    cache.delete(id)
    updateActiveCollections()
    return true
  }

  /**
   * Evict all active collections and stop their syncs.
   * Call this when the user logs out to ensure all streams are terminated.
   */
  function evictAll() {
    const ids = Array.from(cache.keys())
    let evictedCount = 0
    for (const id of ids) {
      if (evict(id)) evictedCount++
    }
    // Also clear the query client cache
    queryClient.clear()
    return evictedCount
  }

  function status(request: CollectionRequest): CollectionStatus | null {
    const builder = builders.get(request.table)
    if (!builder) return null

    const mergedRequest: CollectionBuildRequestConfig = {
      ...builder.baseConfig,
      ...request,
      where: {
        ...(builder.baseConfig.where ?? {}),
        ...(request.where ?? {}),
      },
    }
    const id = buildCollectionId(mergedRequest)
    const entry = cache.get(id)
    return entry ? entry.collection.status : null
  }

  function getBuilderPartitionKey(table: string): string | undefined {
    const builder = builders.get(table)
    return builder?.baseConfig.partitionKey
  }

  ensureBuildersRegistered()

  const api = {
    contextSummary,
    registeredTables,
    activeCollections,
    isInitialized,
    register,
    acquire,
    evict,
    evictAll,
    status,
    getBuilderPartitionKey,
  }

  return api
})

export default useCollectionRegistry
export type CollectionRegistry = ReturnType<typeof useCollectionRegistry>
export type { CollectionAcquisition } from './CollectionRegistry/CollectionAcquisition.interface'
export type { CollectionFactoryContext } from './CollectionRegistry/CollectionFactoryContext.interface'
export type { CollectionKey } from './CollectionRegistry/CollectionKey.interface'
export type { CollectionRequest } from './CollectionRegistry/CollectionRequest.interface'
export type { ReactiveDbApiEndpoints } from './CollectionRegistry/ReactiveDbApiEndpoints.interface'
export { buildWhereClause } from './utils/registry'
